import React, { useEffect, useState, useCallback, useRef, useMemo, useContext } from "react";
import { secureGet, secureDelete, securePost } from "../../config/api";
import { useHistory } from "react-router-dom";
import { formatDateTime } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import OptionsMenu from "../../components/OptionsMenu";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import DeletePopup from "../../components/DeletePopup";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from '../../components/SnackbarNotification';
import { PrimaryButton } from "../../components/PrimaryButton";
import UserContext from "../../Shared/UserContext";

import EyeIcon from "../../icons/eye.svg";
import DeleteIcon from "../../icons/trashR.svg";
import AddIcon from "../../icons/plusB.svg";
import checkIcon from "../../icons/checkB.svg";

const AvoirFinanciers = () => {
  const [, setPaymentsData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    clientId: null,
    commercialId: null,
    dateRange: {},
    needle: "",
    treated: null,
  });
  const [providers, setProviders] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const history = useHistory();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const topRef = useRef(null);
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const STATE_COLORS = {
    true: "#06D6A0",
    false: "#ADAFC6",
  };

  const getStateLabel = (state) => t(`treated_status.${state}`);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const columns = [
    { label: t(`columns.avoirNumber`), key: "avoirFournisseurNumber" },
    {
      label: t("columns.state"), key: "treated",
      render: (treated) => {
        return (
          <span
            className="text-white text-[12px] font-semibold px-2 py-1 rounded-full  inline-block w-fit text-center"
            style={{ backgroundColor: STATE_COLORS[treated] || "#A8ADC3" }}
          >
            {getStateLabel(treated).toUpperCase()}
          </span>
        );
      },
    },
    {
      label: t(`columns.createdAt`), key: "createdAt",
      render: (date) => formatDateTime(date)
    },
    { label: t(`columns.by`), key: "user", render: (user) => `${user.firstName} ${user.lastName}` },
    { label: t(`columns.provider`), key: "supplier", render: (supplier) => supplier?.name },
    { label: "Total TTC", key: "priceTTC" },
    {
      key: "actions",
      render: (_, rowData) =>
        <div onClick={(e) => e.stopPropagation()}>
          <OptionsMenu options={getOptions(rowData)} />
        </div>
    },
  ];

  const getOptions = (item) => {
    const options = [
      ...(role === "ROLE_AGENT" && item.treated === false
        ? [{ label: t("actions.treat"), icon: checkIcon, action: () => handleTreat(item) }]
        : []
      ),
      { label: t(`actions.details`), icon: EyeIcon, action: () => handleDetails(item) },
      ...(role !== 'ROLE_AGENT'
        ? [      { label: t(`actions.delete`), color: '#EF466F', icon: DeleteIcon, action: () => handleDelete(item) },]
        : []
      ),
    ];
    return options;
  };

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
      get: true,
    },
    {
      name: 'providerId',
      label: t(`filters.purchases.provider`),
      type: 'dropdown',
      options: providers,
    },
    {
      name: 'productId',
      label: t(`filters.purchases.product`),
      type: 'dropdown',
      options: products,
    },
    {
      name: "treated",
      label: t("filters.orders.treated"),
      type: "dropdown",
      options: [
        { value: "true", label: t("treated_status.true") },
        { value: "false", label: t("treated_status.false") }
      ]
    }
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const providerResponse = await secureGet('/providers', showSnackbar, { signal });
        const providerOptions = providerResponse.data.map((provider) => ({
          value: provider.id,
          label: provider.name,
        }));
        setProviders(providerOptions);

        const productsResponse = await secureGet("/products/unpaginated", showSnackbar, { signal });
        const productsOptions = productsResponse.data.map((product) => ({
          value: product.id,
          reference: product.reference,
          name: product.name,
        }));
        setProducts(productsOptions);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedPayments = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {

      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('size', size.toString());

      if (activeFilters.needle) {
        params.set('needle', activeFilters.needle);
      }

      if (activeFilters.providerId) {
        params.set('providerId', activeFilters.providerId.toString());
      }

      if (activeFilters.productId) {
        params.set('productId', activeFilters.productId.toString());
      }

      if (activeFilters.startDate) {
        params.set('startDate', activeFilters.startDate);
      }

      if (activeFilters.endDate) {
        params.set('endDate', activeFilters.endDate);
      }

      if (activeFilters.treated) {
        params.set('treated', activeFilters.treated);
      }

      params.set('sort', 'createdAt,desc');

      const response = await secureGet(`/avoirsFournisseur?${params.toString()}`, showSnackbar, {
        signal,
      });

      const { content, totalPages } = response.data;
      setPaymentsData(content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching payments:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setCurrentPage(restoredPage);
      setPageSize(restoredSize);

      fetchPaginatedPayments(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchPaginatedPayments(0, pageSize, filters);
    }
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedPayments(page, pageSize, filters);
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedPayments(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedPayments(0, pageSize, updatedFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      clientId: null,
      commercialId: null,
      dateRange: {},
      needle: "",
      treated: null,
    };
    setFilters(defaultFilters);
    setCurrentPage(0);
    fetchPaginatedPayments(0, pageSize, defaultFilters);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await secureDelete(`/avoirsFournisseur/delete/${itemToDelete.id}`, showSnackbar);
      setDeletePopupVisible(false);
      setItemToDelete(null);
      fetchPaginatedPayments(currentPage, pageSize);
      showSnackbar(t(`confirmation.afournisseurDelete`), 'success');
    } catch (error) {
      console.error("Error deleting avoir fournisseur:", error);
      setDeletePopupVisible(false);
    }
  };

  const handleDetails = (item) => {
    history.push({
      pathname: `/avoirs-fournisseurs/details/${item.id}`,
      state: { afournisseur: item, filters,
        page: currentPage,
        size: pageSize },
    });
  };

  const handleTreat = async (item) => {
    const payload = {
      avoirFinancierId: item.id,
      treated: true
    };
    try {
      const response = await securePost(`/avoirsFournisseur/updateTreated`, payload, showSnackbar);
      showSnackbar(t(`confirmation.treatSuccess`), 'success');
      fetchPaginatedPayments(currentPage, pageSize, filters);
    } catch (error) {
      console.error("Error treating order:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.afournisseur")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] ">
        <h1 className="text-[40px] font-semibold">{t(`titles.afournisseur`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_afournisseur`)}
            icon={AddIcon}
            onClick={() => history.push("/avoirs-fournisseurs/add")}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={(filterName, filterValue) => handleFilterChange(filterName, filterValue)}
        resetFilters={resetFilters}
        filterValues={filters}
      />
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1
            }}>
            <LoadingLogo />
          </div>
        )}
        <Table
          columns={columns}
          data={currentPageData}
          onRowClick={(row) => handleDetails(row)}
        />
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default AvoirFinanciers;